import { PreprocessorUtils, dataflowLexerWoAnno } from './dataflow-preprocessor-utils';
import { PreprocessorsBase } from './dataflow-preprocessors-base';

// The model of scope items which stores the context of our script
// scope means the nearest external paired bracket(of any type)
// item means an argument in scope which is delimited by COMMA
// For example, in [e, f(m, l(k))]
// As for f, the scope is [e, f(m, l(k))] and the item is f(m, l(k))
// As for l, the scope is (m, l(k)) and the item is l(k)
// As for k, the scope is (k) and the item is k
export interface IScopeItem {
    // The start index of scope/item
    scopeIndex: number,		// The start index of this scope(The scope is started with opening brackets)
    itemIndex: number,		// The start index of this item(The first token after opening brackets or commas)
    // (annotations in scope/item) -> (the offset of annotations from start index)
    scopeTags: Map<string, number>,
    itemTags: Map<string, number>
}

// Scope item preprocessor can provide the context about brackets and commas
export class ScopedPreprocessors extends PreprocessorsBase {

    // The stack for each level of nesting
    // For example, in [e, f(m, l(k)), g]
    // At e, the stack is '[e'(scope)/'e'(item)
    // At f, the stack is '[e, f'(scope)/'f'(item)
    // At m, the stack is '[e, f(m'(scope)/'f(m'(item), '(m'(scope)/'m'(item)
    // At k, the stack is '[e, f(m, l(k'(scope)/'f(m, l(k'(item), '(m, l(k'(scope)/'l(k'(item), '(k'(scope)/'k'(item)
    // At g, the stack is '[e, f(m, l(k)), g'(scope)/'g'(item)
    protected scopeItemStack: IScopeItem[];

    constructor(script: string) {
        super(script);
        this.scopeItemStack = [];
    }

    protected get currentScopeItem(): IScopeItem | undefined {
        if (this.scopeItemStack.length < 1){
            return undefined;
        }
        return this.scopeItemStack[this.scopeItemStack.length - 1];
    }

    protected enroll(): void {
        super.enroll();
        this.enrollScopedAnnotationProcessors();	// For tokens closing a scope or an item, add the tags into token model's head
        this.enrollStackPoppingProcessor();		// Deal with scope item stack
        this.enrollScopedProcessors();	// To mark tags with some rules
        this.enrollStackPushingProcessor();		// Deal with scope item stack
    }

    protected enrollScopedProcessors(): void {
        // To override
    }

    private enrollScopedAnnotationProcessors(): void {
        const fetchScopeMarker = () => {
            if (this.scopeItemStack.length > 0) {
                for (const [annotation, offset] of this.currentScopeItem.scopeTags) {
                    this.tokenList[this.currentScopeItem.scopeIndex + offset].head.push(annotation);
                }
            }
        };
        const fetchItemMarker = () => {
            if (this.scopeItemStack.length > 0) {
                for (const [annotation, offset] of this.currentScopeItem.itemTags) {
                    this.tokenList[this.currentScopeItem.itemIndex + offset].head.push(annotation);
                }
            }
        };
        for (const tokenType of PreprocessorUtils.closeScopeTokens) {
            this.enrollOneProcessor(tokenType, fetchScopeMarker);
            this.enrollOneProcessor(tokenType, fetchItemMarker);
        }
        this.enrollOneProcessor(dataflowLexerWoAnno.COMMA, fetchItemMarker);
    }

    private enrollStackPoppingProcessor(): void {
        const popScope = () => {
            if (this.scopeItemStack.length > 0) {
                this.scopeItemStack.pop();
            }
        };
        for (const tokenType of PreprocessorUtils.closeScopeTokens) {
            this.enrollOneProcessor(tokenType, popScope);
        }
    }

    private enrollStackPushingProcessor(): void {
        const pushNewScope = () => {
            this.scopeItemStack.push({
                scopeIndex: this.tokenList.length,
                itemIndex: this.tokenList.length + 1,
                scopeTags: new Map<string, number>(),
                itemTags: new Map<string, number>()
            });
        };
        const pushNewItem = () => {
            if (this.scopeItemStack.length > 0) {
                this.currentScopeItem.itemIndex = this.tokenList.length + 1;
                this.currentScopeItem.itemTags.clear();
            }
        };
        for (const tokenType of PreprocessorUtils.openScopeTokens) {
            this.enrollOneProcessor(tokenType, pushNewScope);
        }
        this.enrollOneProcessor(dataflowLexerWoAnno.COMMA, pushNewItem);
    }
}
